/**
 * Pure coverage-summary derivation for a tournament's games (epic 2, Slice 3).
 * Kept separate from the component so the counting is unit-testable.
 *
 * All fields derive from the loaded games array. A game is "covered" when it has
 * at least one effective coach; "unassigned" when it has none. "Split" coverage
 * means at least one game carries its own game-specific assignees (coaches added
 * beyond the tournament roster), so coverage isn't uniform across the tournament.
 */

import type { TournamentGameDto } from "../../../../types/tournament-games";

export type CoverageSummary = {
  /** Total number of games loaded. */
  total: number;
  /** Games with at least one effective coach. */
  covered: number;
  /** Games with no effective coach. */
  unassigned: number;
  /** True when at least one game has its own game-specific assignees. */
  isSplit: boolean;
};

type GameCoverageFields = Pick<TournamentGameDto, "assignees" | "effectiveCoaches">;

/** Count coverage across a tournament's games. Pure; safe on an empty list. */
export const summarizeCoverage = (games: GameCoverageFields[]): CoverageSummary => {
  let covered = 0;
  let isSplit = false;
  for (const game of games) {
    if (game.effectiveCoaches.length > 0) covered += 1;
    if (game.assignees.length > 0) isSplit = true;
  }
  return {
    total: games.length,
    covered,
    unassigned: games.length - covered,
    isSplit,
  };
};

/**
 * A single plain-language sentence describing coverage, e.g.
 * "6 games · 4 with a coach · 2 unassigned". Appends a split note when some
 * games carry their own coaches. Uses "·" separators and plain words only
 * (no em dashes). Returns an empty string when there are no games.
 */
export const coverageSummaryText = (summary: CoverageSummary): string => {
  if (summary.total === 0) return "";
  const gameWord = summary.total === 1 ? "game" : "games";
  const parts = [
    `${summary.total} ${gameWord}`,
    `${summary.covered} with a coach`,
    `${summary.unassigned} unassigned`,
  ];
  let text = parts.join(" · ");
  if (summary.isSplit) {
    text += ". Some games have their own coaches, so coverage is split.";
  }
  return text;
};
